import { Injectable } from "@angular/core";
import {
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from "@angular/common/http";

import { Observable, throwError } from "rxjs";
import { map, catchError } from "rxjs/operators";
import { ActivatedRoute, Router } from "@angular/router";
import { SharedService } from "../services/shared.service";
import { DeviceFingerprintService } from "../services/device-fingerprint.service";
import { LOCAL_STORAGE_KEYS, url } from "../global/constant";
import { OPTIONAL_REQUEST } from "./http-context-tokens";

@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {
  private readonly errorToastCooldownMs = 3000;
  private lastErrorToastMessage = "";
  private lastErrorToastAt = 0;

  constructor(
    private router: Router,
    private sharedService: SharedService,
    private activatedRoute: ActivatedRoute,
    private deviceFingerprintService: DeviceFingerprintService,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const offsetMinutes = new Date().getTimezoneOffset();
    const token = localStorage.getItem(LOCAL_STORAGE_KEYS.accessToken);
    // A call that brings its own login keeps it (a Performance Ads review link's access-code login).
    if (token && !request.headers.has("Authorization")) {
      request = request.clone({
        headers: request.headers.set("Authorization", "Bearer " + token),
      });
    }
    if (timeZone) {
      request = request.clone({
        headers: request.headers.set("timezone", timeZone),
      });
    }

    if (offsetMinutes) {
      request = request.clone({
        headers: request.headers.set("offsetminutes", offsetMinutes.toString()),
      });
    }

    const fingerprint = this.deviceFingerprintService.getFingerprint();
    if (fingerprint) {
      request = request.clone({
        headers: request.headers.set("x-device-fingerprint", fingerprint),
      });
    }

    request = request.clone({
      headers: request.headers.set("Accept", "application/json"),
    });

    return next.handle(request).pipe(
      map((event: HttpEvent<any>) => {
        // if (event instanceof HttpResponse) {
        // }
        return event;
      }),
      catchError((err: HttpErrorResponse) => {
        // Read status from the HttpErrorResponse first (real HTTP status),
        // falling back to the body's statusCode field for legacy wrapped errors.
        const status = err?.status || err?.error?.statusCode;
        const message =
          err?.error?.error?.message ||
          err?.error?.message ||
          err?.message ||
          "Something went wrong";

        // Pending-invitation block on the login flow is a valid 403 the login
        // screen handles itself (shows a popup). Let it propagate untouched —
        // no dashboard redirect, no generic error toast.
        const isPendingInvitation =
          err?.error?.data?.code === "PENDING_INVITATION";

        if (isPendingInvitation) {
          return throwError(() => err);
        }

        // Optional reads handle their own failure — no redirect, no toast.
        // See OPTIONAL_REQUEST for why.
        if (request.context.get(OPTIONAL_REQUEST) && status === 403) {
          return throwError(() => err);
        }

        const skipErrorToast =
          request.headers.get("X-Skip-Error-Toast") === "1";

        if (status === 401) {
          this.sharedService.clearAuthStorage();
          this.router.navigateByUrl(url.sessionExpired);
        } else if (status === 403) {
          if (this.shouldRedirectForbiddenToDashboard()) {
            this.router.navigateByUrl(url.dashboard);
          }
          if (!skipErrorToast) {
            this.showErrorToastIfAllowed(message);
          }
        } else if (!skipErrorToast) {
          this.showErrorToastIfAllowed(message);
        }
        return throwError(() => err);
      }),
    );
  }

  private showErrorToastIfAllowed(message: string): void {
    const now = Date.now();
    const isDuplicateMessage = message === this.lastErrorToastMessage;
    const inCooldownWindow = now - this.lastErrorToastAt < this.errorToastCooldownMs;

    if (isDuplicateMessage && inCooldownWindow) {
      return;
    }

    this.lastErrorToastMessage = message;
    this.lastErrorToastAt = now;
    this.sharedService.errorToastNotification(message);
  }

  private shouldRedirectForbiddenToDashboard(): boolean {
    const currentUrl = (this.router.url || "").split("?")[0];
    if (!localStorage.getItem(LOCAL_STORAGE_KEYS.accessToken)) {
      return false;
    }
    return !(
      currentUrl.startsWith("/auth") ||
      currentUrl.startsWith("/onboarding") ||
      currentUrl.startsWith("/external") ||
      currentUrl.startsWith("/dairector-org-invite-accept") ||
      currentUrl === url.dashboard
    );
  }
}
